/**
 * DOMI-CARE - Módulo 3: Gestión de Prestadores, RRHH y Agenda de Visitas en Campo
 * Control de habilitaciones profesionales, matrículas, agenda y firma de conformidad en domicilio.
 */

import { offlineStore } from '../../api/offlineStore.js';
import { SEED_DATA } from '../../data/seedData.js';
import { authState } from '../../state/authState.js';
import { ModalManager } from '../../components/modal.js';
import { SignaturePad } from '../../components/signaturePad.js';
import { appsScriptClient } from '../../api/appsScriptClient.js';
import { notify } from '../../components/notificationCenter.js';

let sigProfVisita = null;
let sigFamVisita = null;
let currentVisitaActiva = null;

export async function renderPrestadoresView(container, activePatientId, onSelectPatient) {
  const agenda = await offlineStore.getAll('agenda');
  const prestadores = SEED_DATA.prestadores;
  const user = authState.getActiveUser();

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h1 class="view-title">Gestión de Prestadores, RRHH y Agenda</h1>
        <p class="view-subtitle">Coordinación del equipo tratante interdisciplinario y validación de visitas en campo</p>
      </div>
      <div class="view-actions">
        <button id="btn-nueva-visita-agenda" class="btn btn-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
          Programar Nueva Visita
        </button>
      </div>
    </div>

    <!-- HOJA DE RUTA / AGENDA DEL DÍA -->
    <div class="card" style="margin-bottom: 2rem;">
      <div class="card-header">
        <h3 class="card-title">
          📅 Agenda y Hoja de Ruta Domiciliaria de Hoy (${agenda.length} Visitas)
        </h3>
        <span class="badge badge-primary">Sincronizado</span>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Paciente</th>
              <th>Profesional Tratante</th>
              <th>Disciplina</th>
              <th>Motivo / Práctica</th>
              <th>Nomenclador</th>
              <th>Estado</th>
              <th>Acción en Domicilio</th>
            </tr>
          </thead>
          <tbody>
            ${agenda.map(v => {
              const isDone = v.estado === 'Realizada';
              return `
                <tr style="${isDone ? 'background: #f8fafc; opacity: 0.85;' : ''}">
                  <td><strong style="font-family: var(--font-mono); font-size: 0.95rem;">${v.hora} hs</strong></td>
                  <td>
                    <div style="font-weight: 700; color: var(--text-primary);">${v.pacienteNombre}</div>
                    <div style="font-size: 0.75rem; color: var(--text-muted);">📍 ${v.direccion}</div>
                  </td>
                  <td>${v.prestadorNombre}</td>
                  <td><span class="badge badge-primary">${v.disciplina}</span></td>
                  <td style="font-size: 0.825rem;">${v.motivo}</td>
                  <td><code style="font-size: 0.72rem; background: var(--slate-100); padding: 0.2rem 0.4rem; border-radius: 4px;">${v.codigoNomenclador || 'S/N'}</code></td>
                  <td>
                    <span class="badge ${isDone ? 'badge-success' : 'badge-warning'}">
                      ${isDone ? '✓ Realizada' : 'Pendiente'}
                    </span>
                  </td>
                  <td>
                    ${isDone ? `
                      <span style="font-size: 0.75rem; color: var(--success-600); font-weight: 700;">
                        Firmada (${v.horaEfectiva || 'OK'})
                      </span>
                    ` : `
                      <button class="btn btn-teal btn-sm btn-iniciar-visita" data-id="${v.id}">
                        ✍️ Registrar y Firmar
                      </button>
                    `}
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- EQUIPO TRATANTE / DIRECTORIO DE PROFESIONALES -->
    <div>
      <h3 style="font-size: 1.15rem; font-weight: 800; color: var(--text-primary); margin-bottom: 1rem;">
        🩺 Directorio de Prestadores y Habilitaciones Sanitarias
      </h3>

      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem;">
        ${prestadores.map(p => `
          <div class="card">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
              <div>
                <h4 style="font-size: 1rem; font-weight: 700; color: var(--text-primary);">${p.nombre}</h4>
                <div style="font-size: 0.8rem; color: var(--primary-700); font-weight: 600;">${p.disciplina}</div>
              </div>
              <span class="badge badge-success">${p.estado}</span>
            </div>

            <div style="background: var(--slate-50); padding: 0.6rem; border-radius: var(--radius-md); font-size: 0.78rem; margin-bottom: 0.75rem;">
              <div>📋 <strong>Matrícula:</strong> ${p.matricula} (${p.provincia})</div>
              <div>📞 <strong>Tel:</strong> ${p.telefono}</div>
              <div>✉️ <strong>Email:</strong> ${p.email}</div>
              <div>🗺️ <strong>Zona:</strong> ${p.zona}</div>
            </div>

            <button class="btn btn-secondary btn-sm" style="width: 100%; font-size: 0.78rem;">
              Ver Cartilla y Guardias
            </button>
          </div>
        `).join('')}
      </div>
    </div>

    <!-- Modal para Validar y Firmar Visita en Domicilio -->
    ${renderModalFirmaVisita(user)}
  `;

  // Listeners
  container.querySelectorAll('.btn-iniciar-visita').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const vid = e.currentTarget.dataset.id;
      const visita = agenda.find(v => v.id === vid);
      if (visita) {
        currentVisitaActiva = visita;
        openFirmaModal(visita, user);
      }
    });
  });

  const btnProg = document.getElementById('btn-nueva-visita-agenda');
  if (btnProg) {
    btnProg.addEventListener('click', () => {
      notify.info('Función de asignación inteligente de turnos disponible en panel central.');
    });
  }
}

function renderModalFirmaVisita(user) {
  return `
    <div id="modal-firma-visita" class="modal-overlay">
      <div class="modal-container" style="max-width: 750px;">
        <div class="modal-header">
          <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--text-primary);">
            ✍️ Validación y Doble Firma de Visita en Campo
          </h2>
          <button class="btn btn-secondary btn-sm" onclick="document.getElementById('modal-firma-visita').classList.remove('active')">✕</button>
        </div>
        <form id="form-firma-visita" style="display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden;">
          <div class="modal-body" style="display: flex; flex-direction: column; gap: 1rem; overflow-y: auto; max-height: calc(88vh - 130px); -webkit-overflow-scrolling: touch; padding: 1.25rem 1.5rem;">
            
            <div id="info-visita-box" style="background: var(--slate-50); padding: 0.75rem; border-radius: var(--radius-md); font-size: 0.825rem; border: 1px solid var(--border-subtle);">
              <!-- Dinámico -->
            </div>

            <div class="form-group">
              <label class="form-label">Evolución de la Visita / Procedimiento Realizado *</label>
              <textarea id="visita-evolucion-texto" class="form-control" rows="3" required placeholder="Describe las maniobras kinesiológicas, curaciones o intervenciones realizadas..."></textarea>
            </div>

            <!-- GPS -->
            <div style="display: flex; gap: 0.5rem; align-items: center;">
              <input type="text" id="visita-gps" class="form-control" placeholder="Coordenadas GPS de arribo" readonly style="font-family: var(--font-mono); font-size: 0.8rem;">
              <button type="button" id="btn-gps-visita" class="btn btn-secondary btn-sm">📍 Obtener GPS</button>
            </div>

            <!-- Canvas Firma Profesional -->
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.2rem;">
                <label class="form-label">Firma del Profesional Prestador *</label>
                <button type="button" class="btn btn-secondary btn-sm" style="font-size: 0.7rem;" onclick="window.clearSigProfVisita()">Borrar</button>
              </div>
              <div class="signature-box">
                <canvas id="canvas-visita-prof" class="signature-canvas"></canvas>
                <div class="signature-actions">
                  <span>Firma obligatoria con validez legal</span>
                  <span>${user.nombre}</span>
                </div>
              </div>
            </div>

            <!-- Canvas Firma Conformidad Cuidador -->
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.2rem;">
                <label class="form-label">Firma de Conformidad (Familiar / Cuidador en Domicilio) *</label>
                <button type="button" class="btn btn-secondary btn-sm" style="font-size: 0.7rem;" onclick="window.clearSigFamVisita()">Borrar</button>
              </div>
              <div class="signature-box">
                <canvas id="canvas-visita-fam" class="signature-canvas"></canvas>
                <div class="signature-actions">
                  <span>Conformidad de prestación efectivamente brindada</span>
                  <span>Firma Paciente / Familiar</span>
                </div>
              </div>
              <input type="text" id="visita-aclaracion-fam" class="form-control" style="margin-top: 0.4rem; font-size: 0.8rem;" placeholder="Aclaración de nombre y DNI del firmante" required>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-firma-visita').classList.remove('active')">Cancelar</button>
            <button type="submit" class="btn btn-primary">Finalizar y Transmitir Visita</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function openFirmaModal(visita, user) {
  const box = document.getElementById('info-visita-box');
  box.innerHTML = `
    <div><strong>Paciente:</strong> ${visita.pacienteNombre} • <strong>Dirección:</strong> ${visita.direccion}</div>
    <div><strong>Motivo:</strong> ${visita.motivo} • <strong>Nomenclador:</strong> ${visita.codigoNomenclador}</div>
    <div style="margin-top: 0.3rem; color: var(--primary-700);"><strong>Profesional Asignado:</strong> ${visita.prestadorNombre} (${visita.disciplina})</div>
  `;

  document.getElementById('visita-evolucion-texto').value = `Visita realizada según protocolo. ${visita.motivo}. Buena tolerancia del paciente sin intercurrencias.`;
  document.getElementById('visita-gps').value = visita.gps || '-34.6231, -58.4489 (GPS Checkin Automático)';

  ModalManager.open('modal-firma-visita');

  setTimeout(() => {
    const cProf = document.getElementById('canvas-visita-prof');
    const cFam = document.getElementById('canvas-visita-fam');
    if (cProf) sigProfVisita = new SignaturePad(cProf, { strokeColor: '#0284c7' });
    if (cFam) sigFamVisita = new SignaturePad(cFam, { strokeColor: '#1e293b' });

    window.clearSigProfVisita = () => sigProfVisita?.clear();
    window.clearSigFamVisita = () => sigFamVisita?.clear();
  }, 250);

  const form = document.getElementById('form-firma-visita');
  form.onsubmit = async (e) => {
    e.preventDefault();
    await handleSaveVisitaFirmada(visita, user);
  };
}

async function handleSaveVisitaFirmada(visita, user) {
  const fProf = sigProfVisita ? sigProfVisita.toDataURL() : null;
  const fFam = sigFamVisita ? sigFamVisita.toDataURL() : null;
  const aclaracion = document.getElementById('visita-aclaracion-fam').value || 'Familiar en Domicilio';
  const texto = document.getElementById('visita-evolucion-texto').value;
  const gps = document.getElementById('visita-gps').value;

  const evolucionData = {
    patientId: visita.patientId,
    subjetivoObjetivo: `Visita programada de ${visita.disciplina}. Arribo al domicilio. Paciente en condiciones clínicas estables.`,
    evaluacionDiagnostica: visita.motivo,
    planTratamiento: 'Continuar según cronograma establecido en PAM.',
    practicasRealizadas: texto,
    firmaProfesional: fProf,
    firmaFamiliar: fFam,
    aclaracionFamiliar: aclaracion,
    gps: gps
  };

  await appsScriptClient.saveEvolucion(evolucionData, null, visita.id, user);

  ModalManager.close('modal-firma-visita');
  notify.success(`Visita de ${visita.disciplina} completada y firmada con éxito.`);

  // Trigger custom refresh
  window.dispatchEvent(new CustomEvent('domicare:refresh-views'));
}
